import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CCAuthService } from '@heidelberg/control-center-frontend-integration/auth';
import { TASK_FORM_ROOT_SCRIPT_URL } from './task-form-root-script-url.injection-token';
import { WINDOW } from './window.injection-token';

const TASK_FORM_DIALOG_ROOT_TAG = 'todo-task-form-dialog-root';

const TASK_FORM_LOAD_ERROR_MESSAGE = 'Task form could not be loaded. Please try again.';

const TASK_FORM_AUTH_CONTEXT_PROPERTY = '__todoTaskFormAuthContext';

const TASK_FORM_EVENT = {
    SUCCESS: 'taskFormSuccess',
    CANCEL: 'taskFormCancel',
} as const;

const TASK_FORM_SCRIPT = {
    ID: 'todo-task-form-root-script',
    LOAD_STATUS_DATASET_KEY: 'loadStatus',
    LOAD_TIMEOUT_MS: 15_000,
    LOAD_STATUS: {
        LOADING: 'loading',
        LOADED: 'loaded',
        ERROR: 'error',
    },
} as const;

type TaskFormAuthContextMessage = {
    personToken: string;
    userId: string;
    userEmail: string;
    userLocale: string;
    organizationId: string;
    organizationName: string;
    ccRoles: string[];
};

type HostElementWithAuthContext = HTMLElement & {
    [TASK_FORM_AUTH_CONTEXT_PROPERTY]: TaskFormAuthContextMessage;
};

@Injectable({ providedIn: 'root' })
export class TaskFormWebComponentLauncherService {
    readonly #document = inject(DOCUMENT);
    readonly #authService = inject(CCAuthService);
    readonly #snackBar = inject(MatSnackBar);
    readonly #taskFormRootScriptUrl = inject(TASK_FORM_ROOT_SCRIPT_URL);
    readonly #window = inject(WINDOW);

    #isOpening = false;

    public async open(): Promise<void> {
        if (this.#isOpening) {
            return;
        }
        this.#isOpening = true;
        try {
            await this.openTaskFormDialog();
        } finally {
            this.#isOpening = false;
        }
    }

    private async openTaskFormDialog(): Promise<void> {
        if (this.#document.querySelector(TASK_FORM_DIALOG_ROOT_TAG)) {
            return;
        }

        const authMessage = this.buildAuthContextMessage();
        if (!this.hasCompleteAuthContext(authMessage)) {
            this.showLoadError();
            return;
        }

        const rootElement = this.#document.createElement(TASK_FORM_DIALOG_ROOT_TAG) as HostElementWithAuthContext;
        rootElement[TASK_FORM_AUTH_CONTEXT_PROPERTY] = authMessage;

        const cleanup = (): void => {
            rootElement.remove();
        };

        rootElement.addEventListener(TASK_FORM_EVENT.SUCCESS, cleanup, { once: true });
        rootElement.addEventListener(TASK_FORM_EVENT.CANCEL, cleanup, { once: true });

        try {
            this.#document.body.appendChild(rootElement);
            await this.loadTaskFormRootWebComponent();
        } catch {
            cleanup();
            this.showLoadError();
        }
    }

    private async loadTaskFormRootWebComponent(): Promise<void> {
        if (this.#window.customElements?.get(TASK_FORM_DIALOG_ROOT_TAG)) {
            await this.waitUntilCustomElementIsDefined();
            return;
        }

        const existingScript = this.#document.getElementById(TASK_FORM_SCRIPT.ID) as HTMLScriptElement | null;
        if (existingScript) {
            if (this.getScriptLoadStatus(existingScript) === TASK_FORM_SCRIPT.LOAD_STATUS.ERROR) {
                throw new Error(TASK_FORM_LOAD_ERROR_MESSAGE);
            }

            if (this.getScriptLoadStatus(existingScript) !== TASK_FORM_SCRIPT.LOAD_STATUS.LOADED) {
                await this.waitForScriptLoad(existingScript);
            }

            await this.waitUntilCustomElementIsDefined();
            return;
        }

        const scriptElement = this.#document.createElement('script');
        scriptElement.id = TASK_FORM_SCRIPT.ID;
        scriptElement.type = 'module';
        scriptElement.src = this.#taskFormRootScriptUrl;
        scriptElement.dataset[TASK_FORM_SCRIPT.LOAD_STATUS_DATASET_KEY] = TASK_FORM_SCRIPT.LOAD_STATUS.LOADING;
        this.#document.head.appendChild(scriptElement);

        await this.waitForScriptLoad(scriptElement);
        await this.waitUntilCustomElementIsDefined();
    }

    private async waitUntilCustomElementIsDefined(): Promise<void> {
        await Promise.race([
            this.#window.customElements.whenDefined(TASK_FORM_DIALOG_ROOT_TAG),
            this.createTimeoutPromise(),
        ]);
    }

    private async waitForScriptLoad(scriptElement: HTMLScriptElement): Promise<void> {
        if (this.getScriptLoadStatus(scriptElement) === TASK_FORM_SCRIPT.LOAD_STATUS.LOADED) {
            return;
        }

        if (this.getScriptLoadStatus(scriptElement) === TASK_FORM_SCRIPT.LOAD_STATUS.ERROR) {
            throw new Error(TASK_FORM_LOAD_ERROR_MESSAGE);
        }

        await Promise.race([
            new Promise<void>((resolve, reject) => {
                scriptElement.addEventListener(
                    'load',
                    () => {
                        this.setScriptLoadStatus(scriptElement, TASK_FORM_SCRIPT.LOAD_STATUS.LOADED);
                        resolve();
                    },
                    { once: true }
                );
                scriptElement.addEventListener(
                    'error',
                    () => {
                        this.setScriptLoadStatus(scriptElement, TASK_FORM_SCRIPT.LOAD_STATUS.ERROR);
                        reject(new Error(TASK_FORM_LOAD_ERROR_MESSAGE));
                    },
                    { once: true }
                );
            }),
            this.createTimeoutPromise(),
        ]);
    }

    private buildAuthContextMessage(): TaskFormAuthContextMessage {
        const currentUser = this.#authService.getCurrentUser();
        const currentMembership = this.#authService.getCurrentMembership();

        return {
            personToken: this.#authService.getPersonToken(),
            userId: currentUser?.id ?? '',
            userEmail: currentUser?.email ?? '',
            userLocale: currentUser?.locale ?? '',
            organizationId: currentMembership?.organizationId ?? '',
            organizationName: currentMembership?.organization?.name ?? '',
            ccRoles: currentMembership?.ccRoles ?? [],
        };
    }

    private hasCompleteAuthContext(authContextMessage: TaskFormAuthContextMessage): boolean {
        return !!authContextMessage.personToken && !!authContextMessage.userId && !!authContextMessage.organizationId;
    }

    private getScriptLoadStatus(scriptElement: HTMLScriptElement): string | undefined {
        return scriptElement.dataset[TASK_FORM_SCRIPT.LOAD_STATUS_DATASET_KEY];
    }

    private setScriptLoadStatus(scriptElement: HTMLScriptElement, status: string): void {
        scriptElement.dataset[TASK_FORM_SCRIPT.LOAD_STATUS_DATASET_KEY] = status;
    }

    private createTimeoutPromise(): Promise<never> {
        return new Promise((_, reject) => {
            this.#window.setTimeout(
                () => reject(new Error(TASK_FORM_LOAD_ERROR_MESSAGE)),
                TASK_FORM_SCRIPT.LOAD_TIMEOUT_MS
            );
        });
    }

    private showLoadError(): void {
        this.#snackBar.open(TASK_FORM_LOAD_ERROR_MESSAGE, '', {
            duration: 5_000,
            panelClass: 'hdmui-error',
        });
    }
}
